import { createFileRoute } from "@tanstack/react-router"
import { Pass } from "ipctg-inflow-types/InFlowDB"
import { ValidForIntervalPeriod } from "ipctg-inflow-types/InFlowEnums"

import Card from "@/globalComponents/atoms/Card"
import Heading from "@/globalComponents/atoms/Heading"
import Text from "@/globalComponents/atoms/Text"
import { PurchasedQuotaPackCard } from "@/globalComponents/molecules/productCards/PurchasedQuotaPack"

export const Route = createFileRoute(
  "/qr/$qrLookupID/dev/molecules/date-display"
)({
  staticData: {
    pageName: "Date Display Test - All Combinations",
  },
  component: RouteComponent,
})

function RouteComponent() {
  // Generate test dates for all 12 months and all possible days
  const generateTestDates = () => {
    const testDates: Array<{
      month: number
      day: number
      date: Date
      description: string
    }> = []

    const baseYear = 2026
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ]

    // Days in each month (non-leap year 2026)
    const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]

    for (let month = 0; month < 12; month++) {
      for (let day = 1; day <= daysInMonth[month]; day++) {
        // Create date at end of day (23:59:59) to ensure it's far enough in future
        const date = new Date(baseYear, month, day, 23, 59, 59)

        // Verify the date didn't roll over to next month (handles invalid dates)
        if (date.getMonth() === month) {
          testDates.push({
            month: month + 1,
            day,
            date,
            description: `${monthNames[month]} ${day}, ${baseYear}`,
          })
        }
      }
    }

    return testDates
  }

  // Generate additional edge cases for time remaining display
  const generateTimingTestCases = () => {
    const now = new Date()
    return [
      {
        description: "365+ days (full year away)",
        date: new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000),
      },
      {
        description: "90 days (shows full date)",
        date: new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000),
      },
      {
        description: "60 days (shows full date)",
        date: new Date(now.getTime() + 60 * 24 * 60 * 60 * 1000),
      },
      {
        description: "30+ days (shows full date)",
        date: new Date(now.getTime() + 45 * 24 * 60 * 60 * 1000),
      },
      {
        description: "30 days (boundary)",
        date: new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000),
      },
      {
        description: "15 days",
        date: new Date(now.getTime() + 15 * 24 * 60 * 60 * 1000),
      },
      {
        description: "7 days",
        date: new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000),
      },
      {
        description: "3 days",
        date: new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000),
      },
      {
        description: "2 days (shows days only)",
        date: new Date(now.getTime() + 2 * 24 * 60 * 60 * 1000),
      },
      {
        description: "1 day 12 hours (shows days + hours)",
        date: new Date(now.getTime() + 36 * 60 * 60 * 1000),
      },
      {
        description: "1 day 6 hours",
        date: new Date(now.getTime() + 30 * 60 * 60 * 1000),
      },
      {
        description: "1 day 0 hours (boundary)",
        date: new Date(now.getTime() + 24 * 60 * 60 * 1000),
      },
      {
        description: "23 hours (shows hours only)",
        date: new Date(now.getTime() + 23 * 60 * 60 * 1000),
      },
      {
        description: "12 hours",
        date: new Date(now.getTime() + 12 * 60 * 60 * 1000),
      },
      {
        description: "6 hours",
        date: new Date(now.getTime() + 6 * 60 * 60 * 1000),
      },
      {
        description: "3 hours",
        date: new Date(now.getTime() + 3 * 60 * 60 * 1000),
      },
      {
        description: "1 hour (boundary)",
        date: new Date(now.getTime() + 60 * 60 * 1000),
      },
      {
        description: "59 minutes (shows minutes only)",
        date: new Date(now.getTime() + 59 * 60 * 1000),
      },
      {
        description: "30 minutes",
        date: new Date(now.getTime() + 30 * 60 * 1000),
      },
      {
        description: "15 minutes",
        date: new Date(now.getTime() + 15 * 60 * 1000),
      },
      {
        description: "5 minutes",
        date: new Date(now.getTime() + 5 * 60 * 1000),
      },
      {
        description: "1 minute",
        date: new Date(now.getTime() + 1 * 60 * 1000),
      },
    ]
  }

  const allMonthDayDates = generateTestDates()
  const timingTestCases = generateTimingTestCases()

  // Generate year rollover test cases
  const generateYearRolloverCases = () => {
    return [
      {
        description: "Dec 25, 2026 (Christmas)",
        date: new Date(2026, 11, 25, 23, 59, 59),
      },
      {
        description: "Dec 31, 2026 (New Year's Eve)",
        date: new Date(2026, 11, 31, 23, 59, 59),
      },
      {
        description: "Jan 1, 2027 (New Year's Day)",
        date: new Date(2027, 0, 1, 23, 59, 59),
      },
      {
        description: "Jan 15, 2027",
        date: new Date(2027, 0, 15, 23, 59, 59),
      },
      {
        description: "Feb 14, 2027 (Valentine's Day)",
        date: new Date(2027, 1, 14, 23, 59, 59),
      },
      {
        description: "Feb 29, 2027 (Invalid - not a leap year)",
        date: new Date(2027, 1, 29, 23, 59, 59),
      },
      {
        description: "Dec 31, 2027 (Next year end)",
        date: new Date(2027, 11, 31, 23, 59, 59),
      },
      {
        description: "Jan 1, 2028 (Leap year start)",
        date: new Date(2028, 0, 1, 23, 59, 59),
      },
      {
        description: "Feb 29, 2028 (Valid - leap year)",
        date: new Date(2028, 1, 29, 23, 59, 59),
      },
    ]
  }

  const yearRolloverCases = generateYearRolloverCases()

  // Create mock pass data
  const createMockPass = (
    validToDate: Date,
    id: number,
    description?: string
  ): Pass & {
    action: "redeem" | "warn" | "info" | "none"
    isPending: boolean
    onActionPressed: () => void
  } => {
    return {
      passID: `TEST-${String(id).padStart(4, "0")}`,
      passName: description || `Test Pass`,
      validTo: validToDate.toISOString(),
      maxUses: 10,
      uses: 2,
      passSpecifications: {
        validTo: validToDate.toISOString(),
        maxUses: 10,
        subjectToEventRestrictions: false,
        requiresPassphrase: false,
        redemptionConfig: {
          type: "ValidFor",
          intervalPeriod: ValidForIntervalPeriod.Hour,
          intervalCount: 2,
          maxUses: null,
        },
      },
      action: "redeem",
      isPending: false,
      onActionPressed: () => {},
    } as Pass & {
      action: "redeem" | "warn" | "info" | "none"
      isPending: boolean
      onActionPressed: () => void
    }
  }

  return (
    <>
      <style>{`
        .dev-container > *:not(:first-child) {
          margin-top: var(--space-5);
        }
        .card-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: var(--space-4);
          margin-top: var(--space-4);
        }
        .month-section {
          margin-top: var(--space-5);
        }
        .timing-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: var(--space-4);
          margin-top: var(--space-4);
        }
      `}</style>
      <div className="dev-container">
        <Card>
          <Heading level={1} as={2}>
            Date Display Test - All Combinations
          </Heading>
          <Text>
            This page tests the flex pass date display system for all possible
            month and day combinations, plus various time-remaining scenarios.
          </Text>
        </Card>

        <Card>
          <Heading level={2} as={3}>
            Time Remaining Display Logic
          </Heading>
          <Text>
            The PurchasedQuotaPackCard displays validity information using the
            following logic:
          </Text>
          <ul
            style={{
              marginLeft: "var(--space-5)",
              marginTop: "var(--space-3)",
            }}
          >
            <li>
              <Text>
                <strong>30+ days:</strong> Shows full date (e.g., "valid until
                January 15, 2026")
              </Text>
            </li>
            <li>
              <Text>
                <strong>2-30 days:</strong> Shows days only (e.g., "15 days")
              </Text>
            </li>
            <li>
              <Text>
                <strong>1-2 days:</strong> Shows days + hours (e.g., "1 day 5
                hours")
              </Text>
            </li>
            <li>
              <Text>
                <strong>1-24 hours:</strong> Shows hours only (e.g., "3 hours")
              </Text>
            </li>
            <li>
              <Text>
                <strong>&lt;1 hour:</strong> Shows minutes only (e.g., "45
                minutes")
              </Text>
            </li>
          </ul>
        </Card>

        <Card>
          <Heading level={2} as={3}>
            Time Remaining Test Cases
          </Heading>
          <Text>
            These cards test various time-remaining scenarios to verify the
            display logic boundaries.
          </Text>
          <div className="timing-grid">
            {timingTestCases.map((testCase, index) => (
              <div key={`timing-${index}`}>
                <Text
                  level={3}
                  className="semi-bold"
                  style={{ marginBottom: "var(--space-2)" }}
                >
                  {testCase.description}
                </Text>
                <PurchasedQuotaPackCard
                  {...createMockPass(
                    testCase.date,
                    1000 + index,
                    testCase.description
                  )}
                />
              </div>
            ))}
          </div>
        </Card>

        <Card>
          <Heading level={2} as={3}>
            Year Rollover Test Cases
          </Heading>
          <Text>
            These cards test date display across year boundaries (2026 → 2027 →
            2028), including leap year handling. The Feb 29, 2027 test will roll
            over to March 1, 2027 since 2027 is not a leap year.
          </Text>
          <div className="timing-grid">
            {yearRolloverCases.map((testCase, index) => (
              <div key={`rollover-${index}`}>
                <Text
                  level={3}
                  className="semi-bold"
                  style={{ marginBottom: "var(--space-2)" }}
                >
                  {testCase.description}
                </Text>
                <Text
                  level={3}
                  style={{ marginBottom: "var(--space-2)", opacity: 0.7 }}
                >
                  Actual:{" "}
                  {testCase.date.toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </Text>
                <PurchasedQuotaPackCard
                  {...createMockPass(
                    testCase.date,
                    2000 + index,
                    testCase.description
                  )}
                />
              </div>
            ))}
          </div>
        </Card>

        {/* Group by month */}
        {Array.from({ length: 12 }, (_, monthIndex) => {
          const monthDates = allMonthDayDates.filter(
            (d) => d.month === monthIndex + 1
          )
          const monthName = new Date(2026, monthIndex, 1).toLocaleString(
            "en-US",
            { month: "long" }
          )

          return (
            <Card key={`month-${monthIndex}`} className="month-section">
              <Heading level={2} as={3}>
                {monthName} - All Days
              </Heading>
              <Text>
                Testing all {monthDates.length} days in {monthName} 2026
              </Text>
              <div className="card-grid">
                {monthDates.map((testDate, index) => (
                  <div key={`${testDate.month}-${testDate.day}`}>
                    <Text
                      level={3}
                      className="semi-bold"
                      style={{ marginBottom: "var(--space-2)" }}
                    >
                      Day {testDate.day}
                    </Text>
                    <PurchasedQuotaPackCard
                      {...createMockPass(
                        testDate.date,
                        monthIndex * 100 + index,
                        testDate.description
                      )}
                    />
                  </div>
                ))}
              </div>
            </Card>
          )
        })}

        <Card>
          <Heading level={2} as={3}>
            Test Summary
          </Heading>
          <Text>
            <strong>Total test cases:</strong>{" "}
            {allMonthDayDates.length +
              timingTestCases.length +
              yearRolloverCases.length}
          </Text>
          <ul
            style={{
              marginLeft: "var(--space-5)",
              marginTop: "var(--space-3)",
            }}
          >
            <li>
              <Text>
                Month/Day combinations: {allMonthDayDates.length} (365 dates in
                2026)
              </Text>
            </li>
            <li>
              <Text>Time-remaining edge cases: {timingTestCases.length}</Text>
            </li>
            <li>
              <Text>
                Year rollover cases: {yearRolloverCases.length} (2026-2028)
              </Text>
            </li>
          </ul>
          <Text style={{ marginTop: "var(--space-3)" }}>
            All dates are set to 2026 to ensure they display as "valid until
            [date]" format (30+ days in the future). The timing test cases use
            relative dates from the current time to test the duration display
            logic.
          </Text>
        </Card>
      </div>
    </>
  )
}
